import React from "react";

interface SlideContent {
  title?: string;
  subtitle?: string;
  exitMessage?: string;
  keyPoints?: string[];
  centerEmoji?: string;
  cornerEmojis?: string[];
  bottomText?: string;
  bgColor?: string;
  showGradientOverlay?: boolean;
  showDecorations?: boolean;
  showBottomBar?: boolean;
  onEdit?: (content: SlideContent) => void;
}

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const ExitSlide3 = {
  id: "exit-slide3",
  name: "Exit Slide Style 3",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "",
    },
    {
      id: "exitMessage",
      label: "Exit Message",
      type: "text",
      defaultValue: "",
    },
    {
      id: "keyPoints",
      label: "Key Points (Array)",
      type: "text",
      defaultValue: JSON.stringify(["", "", ""]),
    },
    {
      id: "centerEmoji",
      label: "Center Emoji",
      type: "text",
      defaultValue: "",
    },
    {
      id: "cornerEmojis",
      label: "Corner Emojis (Array)",
      type: "text",
      defaultValue: JSON.stringify(["", "", "", ""]),
    },
    {
      id: "bottomText",
      label: "Bottom Text",
      type: "text",
      defaultValue: "",
    },
    {
      id: "showGradientOverlay",
      label: "Show Gradient Overlay",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "showDecorations",
      label: "Show Decorations",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "showBottomBar",
      label: "Show Bottom Bar",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "",
      subtitle = "",
      exitMessage = "",
      keyPoints = ["", "", ""],
      centerEmoji = "",
      cornerEmojis = ["", "", "", ""],
      bottomText = "",
      showGradientOverlay = true,
      showDecorations = true,
      showBottomBar = true,
      bgColor = "linear-gradient(135deg, #312e81 0%, #4338ca 100%)",
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    const handleKeyPointEdit = (index: number) => (value: string) => {
      const newKeyPoints = [...keyPoints];
      newKeyPoints[index] = value;
      handleEdit("keyPoints")(JSON.stringify(newKeyPoints));
    };

    const handleCornerEmojiEdit = (index: number) => (value: string) => {
      const newCornerEmojis = [...cornerEmojis];
      newCornerEmojis[index] = value;
      handleEdit("cornerEmojis")(JSON.stringify(newCornerEmojis));
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Gradient Overlay */}
        {showGradientOverlay && (
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/20" />
        )}

        {/* Corner Emojis */}
        {showDecorations && (
          <>
            <div className="absolute top-8 left-8 text-4xl transform -rotate-12">
              <EditableDiv
                text={cornerEmojis[0]}
                onEdit={handleCornerEmojiEdit(0)}
                className=""
              />
            </div>
            <div className="absolute top-8 right-8 text-4xl transform rotate-12">
              <EditableDiv
                text={cornerEmojis[1]}
                onEdit={handleCornerEmojiEdit(1)}
                className=""
              />
            </div>
            <div className="absolute bottom-8 left-8 text-4xl transform rotate-12">
              <EditableDiv
                text={cornerEmojis[2]}
                onEdit={handleCornerEmojiEdit(2)}
                className=""
              />
            </div>
            <div className="absolute bottom-8 right-8 text-4xl transform -rotate-12">
              <EditableDiv
                text={cornerEmojis[3]}
                onEdit={handleCornerEmojiEdit(3)}
                className=""
              />
            </div>
          </>
        )}

        {/* Content Container */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full p-12">
          {/* Header */}
          <div className="text-center mb-12">
            <EditableDiv
              text={title}
              onEdit={handleEdit("title")}
              className="text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-indigo-200 to-indigo-100"
            />
            <EditableDiv
              text={subtitle}
              onEdit={handleEdit("subtitle")}
              className="text-xl text-indigo-100/80"
            />
          </div>

          {/* Center Content */}
          <div className="flex flex-col items-center mb-12">
            <div className="text-7xl mb-8">
              <EditableDiv
                text={centerEmoji}
                onEdit={handleEdit("centerEmoji")}
                className="transform hover:scale-110 transition-transform"
              />
            </div>
            <div className="max-w-2xl text-center mb-8">
              <EditableDiv
                text={exitMessage}
                onEdit={handleEdit("exitMessage")}
                className="text-2xl text-indigo-100 leading-relaxed"
              />
            </div>
          </div>

          {/* Key Points */}
          <div className="grid grid-cols-3 gap-8 max-w-4xl w-full mb-12">
            {keyPoints.map((point, idx) => (
              <div
                key={idx}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center transform hover:scale-105 transition-transform"
              >
                <EditableDiv
                  text={point}
                  onEdit={handleKeyPointEdit(idx)}
                  className="text-lg text-indigo-100/90"
                />
              </div>
            ))}
          </div>

          {/* Bottom Bar */}
          {showBottomBar && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/20 to-transparent py-4">
              <div className="text-center">
                <EditableDiv
                  text={bottomText}
                  onEdit={handleEdit("bottomText")}
                  className="text-sm text-indigo-100/70 italic"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
};

declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default ExitSlide3;
